import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  Pressable,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { doc, getDoc } from 'firebase/firestore';
import { useTheme } from '../../contexts/ThemeContext';
import { searchUsersForShare } from '../../utils/calendarShareSendService';
import { getFirestoreDb } from '../../utils/riderShareService';
import { getTabEntryDraftText } from '../../utils/calendarTabDraftService';
import {
  extractContactRecipientsFromRaw,
  mergeRecipientLists,
  recipientLabel,
  tourPartyToRecipients,
} from '../../utils/riderEmailRecipientsUtils';
import { normalizeEmail } from '../../utils/tourPartyMemberUtils';

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function RiderEmailRecipientsModal({
  visible,
  onClose,
  onSend,
  sending = false,
  ownerId,
  tourId,
  selectedDateKey,
  tourPartyMembers = [],
  theme,
}) {
  const { isDarkMode } = useTheme();
  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);
  const db = getFirestoreDb();

  const [recipientMode, setRecipientMode] = useState('platform');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  const [queue, setQueue] = useState([]);
  const [message, setMessage] = useState('');
  const [contactsLoading, setContactsLoading] = useState(false);
  const [dayContacts, setDayContacts] = useState([]);
  const searchSeq = useRef(0);

  const partyRecipients = useMemo(() => tourPartyToRecipients(tourPartyMembers), [tourPartyMembers]);
  const queueEmails = useMemo(() => new Set(queue.map((q) => normalizeEmail(q.email)).filter(Boolean)), [queue]);

  const resetState = useCallback(() => {
    setRecipientMode('platform');
    setSearch('');
    setSearchResults([]);
    setManualEmail('');
    setQueue([]);
    setMessage('');
    setDayContacts([]);
  }, []);

  useEffect(() => {
    if (!visible) {
      resetState();
      return undefined;
    }
    if (!ownerId || !tourId || !selectedDateKey) {
      setDayContacts([]);
      return undefined;
    }
    let cancelled = false;
    setContactsLoading(true);
    (async () => {
      try {
        const entryId = `${tourId}__CONTACTS__${selectedDateKey}`;
        const snap = await getDoc(doc(db, 'users', ownerId, 'calendarTabEntries', entryId));
        if (cancelled) return;
        const raw = snap.exists() ? getTabEntryDraftText(snap.data() || {}) : '';
        setDayContacts(extractContactRecipientsFromRaw(raw));
      } catch (err) {
        console.warn('RiderEmailRecipientsModal contacts load', err);
        if (!cancelled) setDayContacts([]);
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, ownerId, tourId, selectedDateKey, db]);

  const runUserSearch = useCallback(
    async (term) => {
      setSearch(term);
      if (term.trim().length < 2) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      const seq = ++searchSeq.current;
      setSearchLoading(true);
      try {
        const rows = await searchUsersForShare(db, term, {
          excludeUserId: ownerId,
          excludeEmails: [...queueEmails],
        });
        if (seq !== searchSeq.current) return;
        setSearchResults(rows);
      } catch (err) {
        if (seq !== searchSeq.current) return;
        setSearchResults([]);
      } finally {
        if (seq === searchSeq.current) setSearchLoading(false);
      }
    },
    [db, ownerId, queueEmails]
  );

  const visibleSearchResults = useMemo(
    () => searchResults.filter((u) => !queueEmails.has(normalizeEmail(u.email))),
    [searchResults, queueEmails]
  );

  const addToQueue = (person) => {
    const email = normalizeEmail(person.email);
    if (!email || queueEmails.has(email)) return;
    setQueue((prev) => [...prev, { ...person, key: person.key || email, email }]);
  };

  const addUserToQueue = (user) => {
    const email = normalizeEmail(user.email);
    if (!email) return;
    const first = String(user.firstName || '').trim();
    const last = String(user.lastName || '').trim();
    const name = `${first} ${last}`.trim() || String(user.username || '').trim();
    addToQueue({
      key: user.id || email,
      userId: user.id,
      email,
      name,
      source: 'platform',
    });
    setSearch('');
    setSearchResults([]);
  };

  const addManualToQueue = () => {
    const email = normalizeEmail(manualEmail);
    if (!email || !isValidEmail(email)) return;
    addToQueue({ key: `manual-${email}`, email, name: '', source: 'external' });
    setManualEmail('');
  };

  const removeFromQueue = (key) => {
    setQueue((prev) => prev.filter((q) => q.key !== key));
  };

  const addBulk = (list) => {
    setQueue((prev) => mergeRecipientLists(prev, list));
  };

  const partyToAdd = partyRecipients.filter((p) => !queueEmails.has(p.email));
  const contactsToAdd = dayContacts.filter((p) => !queueEmails.has(p.email));
  const bothToAdd = mergeRecipientLists(partyRecipients, dayContacts).filter((p) => !queueEmails.has(p.email));

  const handleSend = () => {
    if (!queue.length) return;
    onSend?.(queue, message.trim());
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => {}}>
          <Text style={styles.title}>Email rider for this day</Text>
          <Text style={styles.hint}>Add recipients one at a time, or use the shortcuts below.</Text>

          <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {queue.length > 0 ? (
              <View style={styles.queueBlock}>
                <Text style={styles.sectionLabel}>Recipients ({queue.length})</Text>
                {queue.map((person) => (
                  <View key={person.key} style={styles.queueRow}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.queueName} numberOfLines={1}>
                        {recipientLabel(person)}
                      </Text>
                      {person.name ? (
                        <Text style={styles.queueSub} numberOfLines={1}>
                          {person.email}
                        </Text>
                      ) : null}
                    </View>
                    <TouchableOpacity onPress={() => removeFromQueue(person.key)} hitSlop={8}>
                      <Ionicons name="close-circle-outline" size={18} color={theme.status.error} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            ) : (
              <Text style={styles.emptyHint}>No recipients yet — search or add an email below.</Text>
            )}

            <View style={styles.bulkRow}>
              <TouchableOpacity
                style={[styles.bulkBtn, partyToAdd.length === 0 && styles.bulkBtnDisabled]}
                onPress={() => addBulk(partyRecipients)}
                disabled={partyRecipients.length === 0}
              >
                <Text style={styles.bulkBtnText}>Tour party</Text>
                <Text style={styles.bulkBtnSub}>{partyRecipients.length || '0'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkBtn, contactsToAdd.length === 0 && contactsLoading && styles.bulkBtnDisabled]}
                onPress={() => addBulk(dayContacts)}
                disabled={contactsLoading || dayContacts.length === 0}
              >
                <Text style={styles.bulkBtnText}>Contacts</Text>
                <Text style={styles.bulkBtnSub}>
                  {contactsLoading ? '…' : dayContacts.length || '0'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkBtn, bothToAdd.length === 0 && styles.bulkBtnDisabled]}
                onPress={() => addBulk(mergeRecipientLists(partyRecipients, dayContacts))}
                disabled={partyRecipients.length === 0 && dayContacts.length === 0}
              >
                <Text style={styles.bulkBtnText}>Both</Text>
                <Text style={styles.bulkBtnSub}>{bothToAdd.length || '0'}</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.modeRow}>
              <TouchableOpacity
                style={[styles.modeBtn, recipientMode === 'platform' && styles.modeBtnActive]}
                onPress={() => setRecipientMode('platform')}
              >
                <Text style={[styles.modeBtnText, recipientMode === 'platform' && styles.modeBtnTextActive]}>
                  VRC member
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modeBtn, recipientMode === 'external' && styles.modeBtnActive]}
                onPress={() => setRecipientMode('external')}
              >
                <Text style={[styles.modeBtnText, recipientMode === 'external' && styles.modeBtnTextActive]}>
                  External email
                </Text>
              </TouchableOpacity>
            </View>

            {recipientMode === 'platform' ? (
              <>
                <View style={styles.searchField}>
                  <Ionicons name="search-outline" size={16} color={theme.text.tertiary} />
                  <TextInput
                    style={styles.searchInput}
                    value={search}
                    onChangeText={runUserSearch}
                    placeholder="Search name, username, or email"
                    placeholderTextColor={theme.input.placeholder}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  {searchLoading ? <ActivityIndicator size="small" color={theme.brand.accent} /> : null}
                </View>
                {search.trim().length >= 2 && !searchLoading && visibleSearchResults.length === 0 ? (
                  <Text style={styles.rowHint}>No members match that search.</Text>
                ) : null}
                {visibleSearchResults.length > 0 ? (
                  <View style={styles.searchResults}>
                    {visibleSearchResults.map((user) => {
                      const first = String(user.firstName || '').trim();
                      const last = String(user.lastName || '').trim();
                      const name = `${first} ${last}`.trim() || user.username || user.email;
                      return (
                        <TouchableOpacity
                          key={user.id}
                          style={styles.searchRow}
                          onPress={() => addUserToQueue(user)}
                        >
                          <View style={{ flex: 1, minWidth: 0 }}>
                            <Text style={styles.searchRowTitle} numberOfLines={1}>
                              {name}
                            </Text>
                            <Text style={styles.searchRowSub} numberOfLines={1}>
                              {user.email}
                            </Text>
                          </View>
                          <Ionicons name="add-circle-outline" size={20} color={theme.brand.accent} />
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ) : null}
              </>
            ) : (
              <View style={styles.addRow}>
                <TextInput
                  style={styles.input}
                  value={manualEmail}
                  onChangeText={setManualEmail}
                  placeholder="email@example.com"
                  placeholderTextColor={theme.input.placeholder}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  onSubmitEditing={addManualToQueue}
                />
                <TouchableOpacity
                  style={styles.addBtn}
                  onPress={addManualToQueue}
                  accessibilityLabel="Add email"
                >
                  <Ionicons name="add" size={18} color="#fff" />
                </TouchableOpacity>
              </View>
            )}

            <Text style={styles.sectionLabel}>Optional note</Text>
            <TextInput
              style={[styles.input, styles.messageInput]}
              value={message}
              onChangeText={setMessage}
              placeholder="Add a short note for the recipient…"
              placeholderTextColor={theme.input.placeholder}
              multiline
            />
          </ScrollView>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.btnGhost} onPress={onClose} disabled={sending}>
              <Text style={{ color: theme.text.secondary, fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btnPrimary, (queue.length === 0 || sending) && { opacity: 0.55 }]}
              onPress={handleSend}
              disabled={queue.length === 0 || sending}
            >
              {sending ? (
                <ActivityIndicator size="small" color={theme.button.primaryText} />
              ) : (
                <Text style={{ color: theme.button.primaryText, fontWeight: '800' }}>
                  {queue.length === 0
                    ? 'Send rider'
                    : `Send to ${queue.length} recipient${queue.length === 1 ? '' : 's'}`}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function createStyles(theme, isDarkMode) {
  const inset = isDarkMode ? 'rgba(255,255,255,0.06)' : theme.background.secondary;
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    },
    card: {
      width: '100%',
      maxWidth: 480,
      maxHeight: '88vh',
      borderRadius: 14,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      padding: 18,
      gap: 10,
    },
    scroll: { maxHeight: 420 },
    title: { fontSize: 18, fontWeight: '800', color: theme.text.primary },
    hint: { fontSize: 13, lineHeight: 18, color: theme.text.secondary },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 4,
    },
    emptyHint: { fontSize: 12, color: theme.text.tertiary, marginBottom: 4 },
    queueBlock: { gap: 6, marginBottom: 4 },
    queueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      backgroundColor: inset,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    queueName: { fontSize: 13, fontWeight: '700', color: theme.text.primary },
    queueSub: { fontSize: 11, color: theme.text.secondary, marginTop: 1 },
    bulkRow: { flexDirection: 'row', gap: 8, marginVertical: 6 },
    bulkBtn: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: 8,
      paddingHorizontal: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: inset,
      gap: 2,
    },
    bulkBtnDisabled: { opacity: 0.45 },
    bulkBtnText: { fontSize: 11, fontWeight: '800', color: theme.text.primary },
    bulkBtnSub: { fontSize: 10, fontWeight: '600', color: theme.text.tertiary },
    modeRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
    modeBtn: {
      flex: 1,
      paddingVertical: 8,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      alignItems: 'center',
      backgroundColor: inset,
    },
    modeBtnActive: {
      borderColor: theme.brand.primary,
      backgroundColor: `${theme.brand.primary}18`,
    },
    modeBtnText: { fontSize: 12, fontWeight: '700', color: theme.text.secondary },
    modeBtnTextActive: { color: theme.brand.primary },
    searchField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      backgroundColor: inset,
    },
    searchInput: { flex: 1, fontSize: 14, color: theme.input.text, padding: 0 },
    searchResults: { gap: 4, marginTop: 4 },
    searchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    searchRowTitle: { fontSize: 13, fontWeight: '700', color: theme.text.primary },
    searchRowSub: { fontSize: 11, color: theme.text.secondary, marginTop: 1 },
    rowHint: { fontSize: 12, color: theme.text.tertiary },
    addRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
    input: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 14,
      color: theme.input.text,
      backgroundColor: inset,
    },
    messageInput: { minHeight: 64, textAlignVertical: 'top' },
    addBtn: {
      width: 40,
      height: 40,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.brand.primary,
    },
    actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
    btnGhost: { paddingVertical: 10, paddingHorizontal: 14 },
    btnPrimary: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: theme.brand.primary,
      minWidth: 120,
      alignItems: 'center',
    },
  });
}
